import ChangeUserPasswordModal from "@/components/change-password/change-user-password-modal.tsx";
import { faCloudDownload, faInfo, faLock, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../contexts/websocket/IcaWebsocketContext';
import profilePic from '../../images/ProfilePic.svg';
import { useGlobalState } from '../../Providers';
import { authLogout, changeUserPassword } from '../../services/loginService';
import { getErrorMessage, showError } from '../../services/utilities/ServiceError';
import { IAppState } from '../../store/types';
import { logout } from '../../store/user-info/actions';
import AboutModal from '../about/AboutModal';
import CopyrightModal from '../about/CopyrightModal';
import LicenseModal from '../about/LicenseModal';
import PluginDownloadModal from '../about/PluginDownloadModal';
import { useDialog } from '../dialog/DialogContext';

interface Props {
    onClose: () => void;
}

const AvatarMenuPanel: FC<Props> = ({ onClose }) => {
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dispatch = useDispatch();
    const icaWebSocket = useIcaWebsocketContext();
    const { translate } = useLocale();
    const { buildInfo } = useGlobalState() ?? {};
    const [openAbout, setOpenAbout] = useState<boolean>(false);
    const [openCopyright, setOpenCopyright] = useState<boolean>(false);
    const [openLicense, setOpenLicense] = useState<boolean>(false);
    const [openChangePassword, setOpenChangePassword] = useState(false);
    const [openPluginDownload, setOpenPluginDownload] = useState(false);
    const dialog = useDialog();

    const handleLogout = async (e) => {
        try {
            authLogout(user.id).then(() => {
                icaWebSocket.disposeConnection();
                e.preventDefault();
                dispatch(logout());
            });
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    const aboutModalButtonClick = (buttonClicked: string) => {
        switch (buttonClicked) {
            case "copyright":
                setOpenAbout(false);
                setOpenCopyright(true);
                break;
            case "license":
                setOpenAbout(false);
                setOpenLicense(true);
                break;
        }
    };

    const onChangePassword = (passwordValues) => {
        changeUserPassword(user.id, passwordValues.newPassword, passwordValues.currentPassword).then(() => {
            toast.success(translate.byKey(TranslationKeys.passwordChangedSuccessfully));
            setOpenChangePassword(false);
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setOpenChangePassword(false);
        });
    };

    return (
        <>
            {/* Transparent overlay catches outside clicks */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={onClose}></div>
            <div className="w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200"
                style={{ position: 'fixed', top: '3.5rem', right: '0.5rem', zIndex: 300 }} >
                <div className="py-1 m-4">
                    <span className="flex-shrink-0 group block">
                        <div className="flex items-center">
                            <div>
                                {user.masterImage
                                    ? <img className="inline-block h-16 w-16 rounded-full object-cover" src={`data:image/png;base64,${user.masterImage}`} alt="" />
                                    : <div className="h-16 w-16 flex flex-col justify-center items-center rounded-full bg-gray-500 text-gray-100">
                                        <img className="h-16 w-16" src={profilePic} alt="" />
                                    </div>
                                }
                            </div>
                            <div className="ml-3">
                                <div className="text-portal-blue-primary text-lg font-semibold" id='usersName'>{user.name}</div>
                                <p className="text-sm font-medium text-gray-800" id='usersProfile'>{user.profile}</p>
                            </div>
                        </div>
                    </span>
                </div>
                <div className="py-1" id='aboutBtnId'>
                    <button
                        onClick={() => setOpenAbout(true)}
                        className="text-gray-700 hover:bg-gray-100 hover:text-gray-900 group flex items-center px-4 py-2 text-sm w-full h-full">
                        <FontAwesomeIcon icon={faInfo} className="mr-5 h-5 w-5 text-gray-900 group-hover:text-gray-500" aria-hidden="true" />
                        {translate.byKey(TranslationKeys.about)}
                    </button>
                </div>
                <div className="py-1" id='pluginDownloadBtnId'>
                    <button
                        onClick={() => setOpenPluginDownload(true)}
                        className="text-gray-700 hover:bg-gray-100 hover:text-gray-900 group flex items-center px-4 py-2 text-sm w-full h-full">
                        <FontAwesomeIcon icon={faCloudDownload} className="mr-5 h-5 w-5 text-gray-900 group-hover:text-gray-500" aria-hidden="true" />
                        {translate.byKey(TranslationKeys.downloads)}
                    </button>
                </div>
                <div className="py-1" id='changePasswordBtnId'>
                    <button
                        onClick={() => setOpenChangePassword(true)}
                        className="text-gray-700 hover:bg-gray-100 hover:text-gray-900 group flex items-center px-4 py-2 text-sm w-full h-full">
                        <FontAwesomeIcon icon={faLock} className="mr-5 h-5 w-5 text-gray-900 group-hover:text-gray-500" aria-hidden="true" />
                        {translate.byKey(TranslationKeys.changePassword)}
                    </button>
                </div>
                <div className="py-1" id='logoutBtnId'>
                    <button
                        onClick={(e) => handleLogout(e)}
                        className="text-gray-700 hover:bg-gray-100 hover:text-gray-900 group flex items-center px-4 py-2 text-sm w-full h-full">
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 h-5 w-5 text-gray-900 group-hover:text-gray-500" aria-hidden="true" />
                        {translate.byKey(TranslationKeys.signOut)}
                    </button>
                </div>
                <div className="py-2 px-4 border-t border-gray-200">
                    <div className="flex justify-between text-xs text-gray-500">
                        <button className="hover:text-blue-600" onClick={() => window.open(buildInfo?.companyWebsite)}>
                            {translate.byKey(TranslationKeys.aboutUs)}
                        </button>
                        <span>{translate.byKey(TranslationKeys.termsAndConditions)}</span>
                    </div>
                </div>
            </div>
            <AboutModal isOpen={openAbout} onClose={() => setOpenAbout(false)} aboutModalButtonClick={aboutModalButtonClick} />
            <CopyrightModal isOpen={openCopyright} onClose={() => setOpenCopyright(false)} />
            <LicenseModal isOpen={openLicense} onClose={() => setOpenLicense(false)} />
            <ChangeUserPasswordModal isOpen={openChangePassword} setIsOpen={setOpenChangePassword} onChangePassword={onChangePassword} />
            <PluginDownloadModal isOpen={openPluginDownload} onClose={() => setOpenPluginDownload(false)} />
        </>
    );
};

export default AvatarMenuPanel;
